import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { parseJsonResponse, FRIENDLY_NETWORK_ERROR } from "./parse-json-response";

export { parseJsonResponse, FRIENDLY_NETWORK_ERROR };

export function invalidateCredits() {
  // The sidebar credits query key may carry a ?brandId= suffix (trial-brand
  // hiding is brand-context-aware), so match by string prefix, not exact key.
  queryClient.invalidateQueries({
    predicate: (q) =>
      typeof q.queryKey[0] === "string" && q.queryKey[0].startsWith("/api/me/credits"),
  });
  queryClient.invalidateQueries({ queryKey: ["/api/me/usage-logs"] });
}

export function invalidateGeneratedAdListings() {
  return queryClient.invalidateQueries({
    predicate: (q) =>
      Array.isArray(q.queryKey) &&
      typeof q.queryKey[0] === "string" &&
      (q.queryKey[0] as string).includes("/generated-ads"),
  });
}

const _origFetch = window.fetch.bind(window);
window.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit) {
  const res = await _origFetch(input, init);
  const url = typeof input === "string" ? input : input instanceof URL ? input.toString() : input.url;
  // API 401 is the one authoritative logout signal. Transport failures and
  // 5xx responses never clear the cached identity, but a server-confirmed
  // invalid session must move every authenticated surface to the login gate.
  if (res.status === 401 && url.startsWith("/api/")) {
    queryClient.setQueryData(["/api/auth/user"], null);
  }
  if (init?.method === "POST" && res.ok) {
    // Promotion is a server-side catalog write. Ads Express keeps its picker
    // cached indefinitely, so invalidate it even if the caller uses fetch
    // rather than apiRequest.
    const promotedBoard = url.match(/^\/api\/brands\/([^/?]+)\/template-studio\/boards\/[^/?]+\/promote(?:\?|$)/);
    if (promotedBoard) {
      queryClient.invalidateQueries({ queryKey: [`/api/brands/${promotedBoard[1]}/ad-units`] });
    }
    if (
      url.startsWith("/api/generate-") ||
      url.startsWith("/api/ai/") ||
      url.includes("/generate-scene") ||
      url.includes("/generate-fingerprint") ||
      url.includes("/generate-image") ||
      url.includes("/ideate-personas") ||
      url.includes("/narrative-generations")
    ) {
      invalidateCredits();
    }
  }
  return res;
} as typeof window.fetch;

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    // A non-JSON error body is almost always the SPA's index.html served in
    // place of a real API response (e.g. an unmatched route during a restart).
    // Dumping raw HTML into the error message just produces the cryptic
    // "Unexpected token '<'" toast, so surface a friendly message instead.
    const contentType = res.headers.get("content-type") || "";
    if (!contentType.includes("application/json")) {
      throw new Error(FRIENDLY_NETWORK_ERROR);
    }
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);

  if (method !== "GET") {
    invalidateCredits();
  }

  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey.join("/") as string, {
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await parseJsonResponse<any>(res);
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
